/** Remediation backlog + reporting profile contracts — mirror backend/src/modules/remediation. */

export type ActionStatus = 'Proposed' | 'Accepted' | 'In_progress' | 'Done' | 'Verified_resolved' | 'Risk_accepted' | 'Not_applicable';

export const ACTION_STATUSES: ActionStatus[] = ['Proposed', 'Accepted', 'In_progress', 'Done', 'Verified_resolved', 'Risk_accepted', 'Not_applicable'];
/** Statuses a person may set (Verified_resolved is set only by an assessment run). */
export const SETTABLE_STATUSES: ActionStatus[] = ['Proposed', 'Accepted', 'In_progress', 'Done', 'Risk_accepted', 'Not_applicable'];

export const STATUS_LABEL: Record<ActionStatus, string> = {
  Proposed: 'Proposed',
  Accepted: 'Accepted',
  In_progress: 'In progress',
  Done: 'Done (awaiting run)',
  Verified_resolved: 'Verified resolved',
  Risk_accepted: 'Risk accepted',
  Not_applicable: 'Not applicable',
};

export interface BacklogRow {
  id: string;
  asset_id: string;
  asset_name: string;
  business_unit: string;
  rec_code: string;
  title: string;
  rationale: string | null;
  priority: number;
  wave: number;
  target_date: string | null;
  due_date: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  owner_text: string | null;
  status: ActionStatus;
  risk_accepted_until: string | null;
  notes: string | null;
  overdue: boolean;
  updated_at: string;
}

export interface BacklogSummary {
  total: number;
  by_status: Record<string, number>;
  by_wave: Array<{ wave: number; total: number; open: number; done: number; overdue: number; unassigned: number }>;
  overdue: number;
  unassigned_open: number;
}

export interface ActionPatch {
  status?: ActionStatus;
  owner_user_id?: string | null;
  owner_text?: string | null;
  due_date?: string | null;
  notes?: string | null;
  risk_accepted_until?: string | null;
}

/** Legacy per-sensitivity appetite (schema 5): still returned for old profiles, ignored since Step 3. */
export interface RiskAppetite { very_high: number; high: number; other: number }
export type PlanningYearBasis = 'project_fixed' | 'curve_at_tolerance' | 'default_2035';

/** Saved reporting profile; null fields fall back to the company record or the recommended default. */
export interface ReportingProfile {
  /** Countries / blocs the organisation operates in; null = the company's country. */
  jurisdictions: string[] | null;
  /** Sectors it is regulated as; null = derived from the company's industry. */
  sectors: string[] | null;
  designations: string[] | null;
  currency: string | null;
  /** Planning year: 'curve' = expert curve at planning_tolerance_pct; 'fixed' = planning_horizon_year. */
  planning_year_mode: 'curve' | 'fixed';
  /** 20 (1 in 5), 33.3 (1 in 3, default) or 50 (1 in 2). */
  planning_tolerance_pct: number;
  /** The fixed planning year (mode 'fixed'). */
  planning_horizon_year: number | null;
  /** Deprecated: ignored since Step 3 (one planning year per project). */
  risk_appetite?: RiskAppetite | null;
  programme_start_date: string | null;
  deadline_basis: 'binding_only' | 'binding_and_guidance';
  /** "Prepared for" on the report covers (name and role); null = the project owner. */
  prepared_for?: string | null;
}

export interface ReportingProfileView extends ReportingProfile {
  effective: {
    jurisdictions: string[]; jurisdictions_source: 'profile' | 'company_country' | 'none';
    sectors: string[]; sectors_source: 'profile' | 'company_industry' | 'none';
    /** The planning year this profile gives against today's curve (each run freezes its own). */
    planning_year: { year: number; basis: PlanningYearBasis; tolerance_pct: number };
    planning_year_options: Array<{ tolerance_pct: number; year: number | null }>;
  };
  organisation: { company_id: string | null; company_name: string | null; country: string | null; industry_type: string | null };
}

export type PqcCommitment = 'none' | 'verbal' | 'written_roadmap' | 'delivered';

export interface VendorCommitmentRow {
  vendor_name: string;
  /** Assets in this project supplied by the vendor. */
  assets: number;
  contract_renewal_date: string | null;
  pqc_commitment: PqcCommitment | null;
  committed_date: string | null;
  notes: string | null;
  /** Where the commitment was taken from (required for every commitment). */
  source_url?: string | null;
  /** 'knowledge_base': QRAM's vendor knowledge base (all projects); 'project': recorded for this project. */
  origin?: 'project' | 'knowledge_base' | null;
  verified_on?: string | null;
  recorded: boolean;
}

export interface RegulatoryMilestoneRow {
  id: string;
  jurisdiction: string;
  issuer: string | null;
  framework: string;
  short_title?: string | null;
  instrument_type: string | null;
  binding: 'mandatory' | 'expected' | 'recommended' | 'reference' | null;
  sectors: string[];
  milestone_date: string | null;
  milestone_kind: string | null;
  scope: string | null;
  category: string | null;
  description: string;
  applies_to: string | null;
  instrument_status: string;
  published: string | null;
  source_url: string;
  verification: 'primary' | 'secondary';
  verified_on: string | null;
  notes: string | null;
}

export interface MandateRow extends RegulatoryMilestoneRow {
  applicability: 'obligation' | 'guidance' | 'reference' | 'other_sector';
  /** A dated completion milestone the deadline basis counts. */
  can_set_deadline: boolean;
  /** The one milestone that sets the deadline test. */
  sets_deadline: boolean;
}
